import { TranslateService } from '@ngx-translate/core';
import { IProject } from 'src/app/shared/interfaces/project/IProject';
import { LocalCurrencyPipe } from 'src/app/shared/pipes/localCurrency.pipe';
import { IFilter } from 'src/app/shared/interfaces/IFilter';
import { COMPARE_OPERATOR } from 'src/app/shared/enums/CompareOperator.enum';
import { IPaginationProps } from 'src/app/shared/interfaces/IPaginationProps';
import { TableFilterOption } from '../../shared/components/table/table.component';
import { CONTROLLING_ACTION } from '../../controlling/enums/controlling-action.enum';
import { FAVORITE_ACTION } from '../../shared/enums/FavoriteActions.enum';
import { PROJECT_ACTION } from '../../shared/enums/ProjectAction.enum';
import { NotificationService } from '../../shared/services/notification.service';
import { IEmployeeSCDItem } from '../../shared/interfaces/IEmployeeSCDItem';

export class ProjectOperations {
  public static getAssignment(projects: IProject[]): IProject[] {
    projects.forEach(project => {
      project.projectLocked = new Date(project.endDate) < new Date();
      project.assignmentStatus = project.projectType && project.hasAccount ? 'done' : '';
    });
    return projects;
  }

  public static populateAggregatedValuesForProjectList(
    projects: IProject[],
    translateService: TranslateService,
    includeProjectComment = false,
  ) {
    projects.forEach(singleProject => {
      if (singleProject.projectsFinance) {
        let totalCosts = singleProject.projectsFinance.currentMonthCosts ? singleProject.projectsFinance.currentMonthCosts : 0;
        const totalCosts2 = singleProject.projectsFinance.previousonthCosts ? singleProject.projectsFinance.previousonthCosts : 0;
        const totalCosts3 = singleProject.projectsFinance.currentMonthTravel ? singleProject.projectsFinance.currentMonthTravel : 0;
        const totalCosts4 = singleProject.projectsFinance.previousonthTravel ? singleProject.projectsFinance.previousonthTravel : 0;
        const totalCosts5 = singleProject.projectsFinance.currentMonthOtherCost ? singleProject.projectsFinance.currentMonthOtherCost : 0;
        const totalCosts6 = singleProject.projectsFinance.previousMonthOtherCost ? singleProject.projectsFinance.previousMonthOtherCost : 0;
        const totalCosts7 = singleProject.projectsFinance.previewMonthCosts ? singleProject.projectsFinance.previewMonthCosts : 0;
        const totalCosts8 = singleProject.projectsFinance.previewMonthTravel ? singleProject.projectsFinance.previewMonthTravel : 0;
        const totalCosts9 = singleProject.projectsFinance.previewMonthOtherCost ? singleProject.projectsFinance.previewMonthOtherCost : 0;
        totalCosts =
          totalCosts + totalCosts2 + totalCosts3 + totalCosts4 + totalCosts5 + totalCosts6 + totalCosts7 + totalCosts8 + totalCosts9;
        const projectBudget = singleProject.budgetSum ? singleProject.budgetSum : 0;
        singleProject.pue = totalCosts > projectBudget ? 'red' : 'green';
        singleProject.tooltipText = this.getProjectStatusTooltip(singleProject, translateService, includeProjectComment);
      }
    });
  }

  public static populateAggregatedValues(project: IProject, isPreviewAvailable = false): IProject {
    if (project?.accountingInformation) {
      project.accountingInformation.previousMonths.actualSumMonth =
        project.accountingInformation.previousMonths.actualProjectCostsMonth +
        project.accountingInformation.previousMonths.actualOtherCostsMonth +
        project.accountingInformation.previousMonths.actualTravelCostsMonth;
      project.accountingInformation.currentMonth.actualSumMonth =
        project.accountingInformation.currentMonth.actualProjectCostsMonth +
        project.accountingInformation.currentMonth.actualTravelCostsMonth +
        project.accountingInformation.currentMonth.actualOtherCostsMonth;
      if (project.accountingInformation.previewMonths) {
        project.accountingInformation.previewMonths.actualSumMonth =
          project.accountingInformation.previewMonths.actualProjectCostsMonth +
          project.accountingInformation.previewMonths.actualTravelCostsMonth +
          project.accountingInformation.previewMonths.actualOtherCostsMonth;
      }
      project.accountingInformation.actualSum =
        project.accountingInformation.previousMonths.actualSumMonth +
        project.accountingInformation.currentMonth.actualSumMonth +
        (project.accountingInformation.previewMonths?.actualSumMonth ?? 0);
      project.accountingInformation.partialBudget = this.calculatePartialBudget(project, isPreviewAvailable);
      project.accountingInformation.partialPercentage =
        project.accountingInformation.actualSum / project.accountingInformation.partialBudget;
    }
    return project;
  }

  private static calculatePartialBudget(project: IProject, isPreviewAvailable = false): number {
    let accountingMonth = new Date(localStorage.getItem('accountingMonth'));
    if (isPreviewAvailable) {
      const now = new Date();
      accountingMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    }
    if (accountingMonth < new Date(project.startDate)) {
      return 0;
    }
    if (accountingMonth >= new Date(project.endDate)) {
      return project.budgetSum;
    }
    let year1 = new Date(project.startDate).getFullYear();
    let year2 = new Date(project.endDate).getFullYear();
    let month1 = new Date(project.startDate).getMonth();
    let month2 = new Date(project.endDate).getMonth();
    if (month2 === 0) {
      month2 = 12;
      year2--;
    }

    if (month1 === 0) {
      month1++;
      month2++;
    }
    if (year2 > year1 && month1 > month2) {
      month2++;
    }
    const numberOfMonthsforProject = (year2 - year1) * 12 + (month2 - month1);
    let startofFiscalYear = new Date();
    if (startofFiscalYear.getMonth() < 10) {
      startofFiscalYear = new Date(startofFiscalYear.getFullYear() - 1 + '-10-01');
    } else {
      startofFiscalYear = new Date(startofFiscalYear.getFullYear() + '-10-01');
    }
    let actualStartDate;
    if (new Date(project.startDate) > startofFiscalYear) {
      actualStartDate = new Date(project.startDate);
    } else {
      actualStartDate = startofFiscalYear;
    }
    year1 = new Date(actualStartDate).getFullYear();
    year2 = accountingMonth.getFullYear();
    month1 = new Date(actualStartDate).getMonth();
    month2 = accountingMonth.getMonth() + 1;
    if (month1 === 0) {
      month1++;
      month2++;
    }
    const numberOfMonthsforFiscalYear = (year2 - year1) * 12 + (month2 - month1);
    return (project.budgetSum / numberOfMonthsforProject) * numberOfMonthsforFiscalYear;
  }

  private static getProjectStatusTooltip(element: IProject, translateService: TranslateService, includeProjectComment: boolean): string {
    let budget: 0 | string;
    let actualDifference: 0 | string;
    let totalCostsSum: 0 | string;

    const currencyPipe = new LocalCurrencyPipe(translateService);

    if (element.projectsFinance) {
      let totalCosts = element.projectsFinance.currentMonthCosts ? element.projectsFinance.currentMonthCosts : 0;
      const totalCosts2 = element.projectsFinance.previousonthCosts ? element.projectsFinance.previousonthCosts : 0;
      const totalCosts3 = element.projectsFinance.currentMonthTravel ? element.projectsFinance.currentMonthTravel : 0;
      const totalCosts4 = element.projectsFinance.previousonthTravel ? element.projectsFinance.previousonthTravel : 0;
      const totalCosts5 = element.projectsFinance.currentMonthOtherCost ? element.projectsFinance.currentMonthOtherCost : 0;
      const totalCosts6 = element.projectsFinance.previousMonthOtherCost ? element.projectsFinance.previousMonthOtherCost : 0;
      const totalCosts7 = element.projectsFinance.previewMonthCosts ? element.projectsFinance.previewMonthCosts : 0;
      const totalCosts8 = element.projectsFinance.previewMonthTravel ? element.projectsFinance.previewMonthTravel : 0;
      const totalCosts9 = element.projectsFinance.previewMonthOtherCost ? element.projectsFinance.previewMonthOtherCost : 0;
      totalCosts =
        totalCosts + totalCosts2 + totalCosts3 + totalCosts4 + totalCosts5 + totalCosts6 + totalCosts7 + totalCosts8 + totalCosts9;
      const projectBudget = element.budgetSum ? element.budgetSum : 0;
      const actualDifference2 = projectBudget - totalCosts;
      budget = projectBudget ? currencyPipe.transform(projectBudget) : 0;
      totalCostsSum = totalCosts ? currencyPipe.transform(totalCosts) : 0;
      actualDifference = actualDifference2 ? currencyPipe.transform(actualDifference2) : 0;

      return (
        translateService.instant('project.tooltip.budget') +
        ': ' +
        budget +
        '\n' +
        translateService.instant('project.tooltip.costs') +
        ': ' +
        totalCostsSum +
        '\n' +
        translateService.instant('project.tooltip.difference') +
        ': ' +
        actualDifference +
        (includeProjectComment ? '\n' + translateService.instant('externals.comment') + ': ' + (element as any)?.projectComment : '')
      );
    }
    return '';
  }

  public static filterUniqueEmployees(employees: IProject['projectOwner'][]): IProject['projectOwner'][] {
    const uniqueEmployees: IProject['projectOwner'][] = [];

    for (const employee of employees) {
      if (employee) {
        const isUnique = !uniqueEmployees.find(uniqueEmployee => uniqueEmployee.scdGid === employee.scdGid);

        if (isUnique) {
          uniqueEmployees.push(employee);
        }
      }
    }

    return uniqueEmployees;
  }

  public static getProjectNumbersFilterOptions(projects: IProject[]): TableFilterOption[] {
    return projects.map(project => {
      return {
        label: project?.projectNumber ? project.projectNumber.toString() : null,
        value: project?.projectNumber ? project.projectNumber.toString() : null,
      };
    });
  }

  public static getUniqueDates(projects: IProject[]): { startDates: string[]; endDates: string[] } {
    // Create Sets to store unique dates
    const startDatesSet = new Set<string>();
    const endDatesSet = new Set<string>();

    projects.forEach(project => {
      if (project.startDate) {
        // Convert to yyyy-MM-DD format to ignore timezone
        const startDate = new Date(project.startDate);
        const startDateString = startDate.toISOString().split('T')[0];
        startDatesSet.add(startDateString);
      }

      if (project.endDate) {
        // Convert to yyyy-MM-DD format to ignore timezone
        const endDate = new Date(project.endDate);
        const endDateString = endDate.toISOString().split('T')[0];
        endDatesSet.add(endDateString);
      }
    });

    return { startDates: Array.from(startDatesSet), endDates: Array.from(endDatesSet) };
  }

  public static getPKTooltipText(project: IProject, translateService: TranslateService): string {
    if (project.hasAccount) {
      return translateService.instant('project.accounts.title') as string;
    } else if (!project.hasAccount && project.assignmentStatus !== 'remove') {
      return translateService.instant('project.assignAccounts') as string;
    } else if (project.assignmentStatus === 'remove') {
      return translateService.instant('project.removeAccounts') as string;
    }
    return '';
  }

  public static showNotification(
    type: CONTROLLING_ACTION | FAVORITE_ACTION | PROJECT_ACTION,
    notificationService: NotificationService,
    employeeNames?: string,
  ): void {
    switch (type) {
      case FAVORITE_ACTION.ADD_FAVORITE: {
        notificationService.showMessage({
          type: 'Success',
          title: 'default.success',
          desc: 'notifications.controlling.addFavorite',
        });
        break;
      }
      case FAVORITE_ACTION.REMOVE_FAVORITE: {
        notificationService.showMessage({
          type: 'Success',
          title: 'default.success',
          desc: 'notifications.controlling.removeFavorite',
        });
        break;
      }
      case PROJECT_ACTION.REOPEN_PROJECT_SUCCESS: {
        notificationService.showMessage({
          type: 'Success',
          title: 'default.success',
          desc: 'notifications.projects.reopenProjectSuccess',
        });
        break;
      }
      case PROJECT_ACTION.REOPEN_PROJECT_ERROR: {
        notificationService.showMessage({
          type: 'Error',
          title: 'default.error',
          desc: 'notifications.projects.reopenProjectError',
        });
        break;
      }
      case PROJECT_ACTION.CLOSE_PROJECT_SUCCESS: {
        notificationService.showMessage({
          type: 'Success',
          title: 'default.success',
          desc: 'notifications.projects.closeProjectSuccess',
        });
        break;
      }
      case PROJECT_ACTION.CLOSE_PROJECT_ERROR: {
        notificationService.showMessage({
          type: 'Error',
          title: 'default.error',
          desc: 'notifications.projects.closeProjectError',
        });
        break;
      }
      case PROJECT_ACTION.DELETE_PROJECT_ERROR: {
        notificationService.showMessage(
          {
            type: 'Error',
            title: 'default.error',
            desc: 'notifications.projects.deleteProjectError',
          },
          null,
          5000,
        );
        break;
      }
      case PROJECT_ACTION.DELETE_PROJECT_SUCCESS: {
        notificationService.showMessage({
          type: 'Success',
          title: 'default.success',
          desc: 'notifications.projects.deleteProjectSuccess',
        });
        break;
      }
      case PROJECT_ACTION.MEMBERS_TIMESHEET_CLOSED_WARNING: {
        notificationService.showMessage(
          {
            type: 'Warning',
            title: 'project.membersWarning.title',
            desc: 'notifications.projects.membersTimesheetClosedWarning',
          },
          { employeeNames },
          5000,
        );
        break;
      }
      case PROJECT_ACTION.PROJECT_NOT_FOUND: {
        notificationService.showMessage({
          type: 'Error',
          title: 'navigation.projects',
          desc: 'project.notFound',
        });
        break;
      }
      case PROJECT_ACTION.START_DATE_CONFLICT_ERROR: {
        notificationService.showMessage({
          type: 'Error',
          title: 'default.error',
          desc: 'notifications.projects.startDateConflict',
        });
        break;
      }
      case PROJECT_ACTION.ERROR_UPDATING: {
        notificationService.showMessage({
          type: 'Error',
          title: 'project.project',
          desc: 'project.updatingError',
        });
        break;
      }
    }
  }

  public static validateNewMembersSettlement(newMembers: IEmployeeSCDItem[], project: IProject): string[] {
    const membersRequiringAction: string[] = [];

    if (!newMembers || newMembers.length === 0) {
      return membersRequiringAction;
    }

    const projectStartDate = project?.startDate ? new Date(project.startDate) : undefined;
    const currentDate = new Date();

    for (const member of newMembers) {
      if (!member.lastMonthlySettlement) {
        continue;
      }

      const lastSettlementDate = new Date(member.lastMonthlySettlement);

      const isSameMonthOrAfter =
        lastSettlementDate.getFullYear() > currentDate.getFullYear() ||
        (lastSettlementDate.getFullYear() === currentDate.getFullYear() && lastSettlementDate.getMonth() >= currentDate.getMonth());

      if (
        isSameMonthOrAfter &&
        projectStartDate &&
        (projectStartDate.getFullYear() < lastSettlementDate.getFullYear() ||
          (projectStartDate.getFullYear() === lastSettlementDate.getFullYear() &&
            projectStartDate.getMonth() <= lastSettlementDate.getMonth()))
      ) {
        membersRequiringAction.push(member.scdName);
      }
    }

    return membersRequiringAction;
  }

  public static buildProjectsPaginationProps(
    fiscalYear: string,
    pageSize: number,
    filters: IFilter[],
    lastRead?: number,
    sort?: string,
    sortedLastReadValue?: string,
    searchValue?: string,
    comparisonOperator?: COMPARE_OPERATOR | '',
    searchFields?: string[],
  ): IPaginationProps {
    // Input validation
    if (!fiscalYear) {
      throw new Error('fiscalYear is required');
    }

    if (!pageSize || pageSize <= 0) {
      throw new Error('pageSize must be a positive number');
    }

    if (!Array.isArray(filters)) {
      throw new Error('filters must be an array');
    }

    return {
      fiscalYear: fiscalYear.trim(),
      pageSize: Math.max(1, pageSize),
      filters,
      lastRead: lastRead ?? 0,
      sort: sort ?? null,
      sortedLastReadValue: sortedLastReadValue ?? null,
      searchValue: searchValue ?? null,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      comparisonOperator: comparisonOperator ?? null,
      searchFields: searchFields ?? [],
    };
  }

  public static getFrontendCalculatedFilterOptions(column: string): TableFilterOption[] {
    switch (column) {
      case 'pue':
        return [
          { label: 'project.red', value: 'red' },
          { label: 'project.green', value: 'green' },
        ];
      case 'pk':
        return [
          { label: 'project.accountAvailable', value: 'true' },
          { label: 'project.accountNotAvailable', value: 'false' },
        ];
      case 'sp':
        return [
          { label: 'default.checked', value: 'true' },
          { label: 'default.unchecked', value: 'false' },
        ];
      default:
        return [];
    }
  }

  public static transformFrontendFiltersForBackend(filters: IFilter[]): IFilter[] {
    return filters.map(filter => {
      if (filter.key === 'pue') {
        return this.transformPueFilter(filter);
      } else {
        return filter;
      }
    });
  }

  private static transformPueFilter(filter: IFilter): IFilter {
    if (filter.values.includes('red') && filter.values.includes('green')) {
      return { ...filter, values: [] };
    } else if (filter.values.includes('red')) {
      return { ...filter, key: 'pue', values: ['true'] };
    } else if (filter.values.includes('green')) {
      return { ...filter, key: 'pue', values: ['false'] };
    }
    return { ...filter, values: [] };
  }
}
